import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useDebounce from '~/hooks/useDebounce';
import type {
  Biedwijzer_AppConfig,
  Device,
  InitialState,
  StepName,
} from '../../types';

/**
 * Breakpoints, they are mirrored in biedwijzer.
 */
export const Breakpoints = {
  mobile: 640, // phones
  tablet: 768, // tablets portrait
  tabletLandscape: 1024, // tablets landscape / small laptops
  desktop: 1280, // laptops / desktops
  desktopLarge: 1536, // hi-res laptops / large desktops
  desktopHD: 1920, // Full HD monitors
  desktopQHD: 2560, // QHD / ultrawide
  desktop4K: 3840, // 4K UHD monitors
};

export type Props = {
  device: Device;
  step: StepName;
  width?: number;
  height?: number;
  data: {
    appConfigData?: Partial<Biedwijzer_AppConfig>;
    initialState?: Partial<InitialState>;
  } | null;
};

const Preview: React.FCC<Props> = ({
  device,
  step,
  height: containerHeight,
  width: containerWidth,
  data: oData,
  ...rest
}) => {
  const { name: accountName, id: accountId } = useCurrentAccount();

  const previewContainer = useRef<HTMLDivElement>(null);
  const deviceWidth = Breakpoints[device];

  const data = useDebounce(oData, 500);

  const [{ initialState, appConfig }, setConfig] = useState({
    initialState: encodeURIComponent(
      JSON.stringify({
        step,
        previewMode: true,
        selectedProperty: data?.initialState?.selectedProperty,
      } as InitialState),
    ),
    appConfig: encodeURIComponent(
      JSON.stringify({
        ...data?.appConfigData,
      }),
    ),
  });

  useLayoutEffect(() => {
    const initialStateJSON = JSON.stringify({
      step,
      previewMode: true,
      selectedProperty: data?.initialState?.selectedProperty,
    });
    const initialState = encodeURIComponent(initialStateJSON);

    const appConfigJson = data?.appConfigData
      ? JSON.stringify(data?.appConfigData)
      : '{}';

    const appConfig = encodeURIComponent(appConfigJson);

    setConfig({
      initialState,
      appConfig,
    });
  }, [step, accountName, data]);

  const [size, setSize] = useState({ width: 1, height: 1 });
  useEffect(() => {
    setSize({
      height: previewContainer.current?.offsetHeight ?? 1,
      width: previewContainer.current?.offsetWidth ?? 1,
    });
    previewContainer.current?.offsetWidth;
  }, [previewContainer]);

  const scale = size.width / deviceWidth;
  const height = size.height * (1 / scale);

  const baseUrl = process.env.BDWZR_URL;
  const url = `${baseUrl}/${accountId}/?initialStateId=0&initialState=${initialState}&appConfig=${appConfig}`;

  return (
    <Container
      $height={containerHeight}
      $width={containerWidth}
      ref={previewContainer}
      {...rest}
    >
      <PreviewContainer
        height={height}
        width={deviceWidth}
        style={{ transform: `scale(${scale})` }}
        src={url}
      />
    </Container>
  );
};

const PreviewContainer = styled.iframe<{}>(
  () => css`
    transform: scale(0.1);
    transform-origin: 0 0;
    border: none;
  `,
);

const Container = styled.div<{ $height?: number; $width?: number }>(
  ({ theme, $height, $width }) => css`
    box-shadow: ${theme.boxShadow('around')};
    border-top-left-radius: ${theme.getTokens().border.radius.base};
    border-top-right-radius: ${theme.getTokens().border.radius.base};
    display: inline-block;
    height: ${$height ? `${$height}px` : '100%'};
    width: ${$width ? `${$width}px` : '100%'};
    overflow: hidden;
    background-color: ${theme.color('white')};
    margin-top: ${theme.space('l')};
  `,
);

export default Preview;
